import { useState } from "react";
import { Alert, Box, Button, Chip, Grid, Stack, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import dayjs from "dayjs";
import { toast } from "react-toastify";

import { BucketAwtENUM, useUploadFile } from "@/shared";
import { CustomPaperBox } from "@/shared/components/ui";
import { ScrollableDialogProps } from "@/shared/components/ui/Dialogs";
import ImagesWithZoom from "@/shared/components/ui/Images/ImagesWithZoom";
import { useUiStore } from "@/store/ui";
import { OrdenTrabajo } from "@/shared/interfaces";
import FotosVisitaMultiUploader from "../FotosVisitaMultiUploader";
import {
  FotoTrasladoNueva,
  agregarFotosTraslado,
  useRefrescarFotosTrasladoLive,
  useSetFotosTrasladoLive,
} from "./fotos-traslado.actions";
import { FOTOS_PROCESO_SLOTS, TipoFotoProceso, fotoProcesoPorTipo } from "./fotos-traslado.utils";

type SubirFotosProcesoTrasladoModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  ordenTrabajo?: OrdenTrabajo;
};

/**
 * Modal para las "Fotos de proceso" del traslado: DOS fotos con título fijo,
 * "Foto punto inicial" y "Foto punto final". Cada una se carga por separado y
 * se guarda en `urls_fotos_proceso` con su `tipo`.
 *
 * El técnico solo carga los puntos PENDIENTES: una foto ya cargada no se puede
 * reemplazar ni quitar desde aquí (solo Activaciones la elimina, y entonces el
 * punto vuelve a quedar pendiente). Se guarda con el endpoint dedicado, que
 * agrega sin pisar lo que haya cambiado mientras tanto.
 */
export default function SubirFotosProcesoTrasladoModal({
  open,
  onClose,
  onSuccess,
  ordenTrabajo,
}: SubirFotosProcesoTrasladoModalProps) {
  const [pendientes, setPendientes] = useState<Record<TipoFotoProceso, File[]>>({
    PUNTO_INICIAL: [],
    PUNTO_FINAL: [],
  });
  const setIsGlobalLoading = useUiStore((s) => s.setIsGlobalLoading);
  const { handleUploadFile } = useUploadFile();
  const setFotosLive = useSetFotosTrasladoLive();
  const refrescarFotosLive = useRefrescarFotosTrasladoLive();

  const idOT = ordenTrabajo?.id_orden_trabajo;
  const PATH_BUCKET = `${BucketAwtENUM.IMAGE_INSTALACION}/${idOT}`;
  // Solo cuentan las selecciones de puntos que siguen pendientes.
  const slotsConSeleccion = FOTOS_PROCESO_SLOTS.filter(
    (s) => pendientes[s.tipo].length > 0 && !fotoProcesoPorTipo(ordenTrabajo, s.tipo),
  );
  const haySeleccion = slotsConSeleccion.length > 0;

  const setSlot = (tipo: TipoFotoProceso, files: File[]) =>
    setPendientes((prev) => ({ ...prev, [tipo]: files.slice(0, 1) }));

  const limpiar = () => setPendientes({ PUNTO_INICIAL: [], PUNTO_FINAL: [] });

  const handleConfirm = async () => {
    if (!idOT) return;
    if (!haySeleccion) {
      toast.info("Selecciona al menos una de las fotos de proceso pendientes.");
      return;
    }
    setIsGlobalLoading(true);
    try {
      const fotos: FotoTrasladoNueva[] = [];
      for (const slot of slotsConSeleccion) {
        const r = await handleUploadFile(pendientes[slot.tipo][0], PATH_BUCKET);
        if (!r?.url) {
          toast.error(`No se pudo subir "${slot.titulo}". Inténtalo de nuevo.`);
          return;
        }
        fotos.push({
          photoUrl: r.url,
          photoId: r?.id_archivos || r.url,
          fecha_hora: dayjs().toISOString(),
          tipo: slot.tipo,
        });
      }
      const snapshot = await agregarFotosTraslado(idOT, { etapa: "PROCESO", fotos });
      setFotosLive(snapshot);
      toast.success("Fotos de proceso guardadas correctamente");
      limpiar();
      onClose();
      onSuccess();
    } catch (e) {
      // El motivo ya lo mostró el cliente HTTP; se refresca el estado por si el
      // punto dejó de estar pendiente.
      console.error("Error subir fotos proceso traslado", e);
      refrescarFotosLive(idOT);
    } finally {
      setIsGlobalLoading(false);
    }
  };

  return (
    <ScrollableDialogProps
      open={open}
      title="Fotos de proceso del traslado"
      minWidth="60%"
      contentNode={
        <CustomPaperBox sxPaper={{ p: 3 }}>
          <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
            Se requieren <b>2 fotos de proceso</b>: <b>Foto punto inicial</b> y{" "}
            <b>Foto punto final</b>. Ambas son obligatorias para poder finalizar el
            traslado y puedes cargarlas por separado. Una foto ya cargada solo la
            puede eliminar Activaciones.
          </Alert>
          <Grid container spacing={2}>
            {FOTOS_PROCESO_SLOTS.map((slot) => {
              const actual = fotoProcesoPorTipo(ordenTrabajo, slot.tipo);
              const seleccion = pendientes[slot.tipo];
              return (
                <Grid item xs={12} md={6} key={slot.tipo}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: actual || seleccion.length ? "success.light" : "divider",
                      height: "100%",
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                      <Typography variant="subtitle2" fontWeight={800}>
                        {slot.titulo}
                      </Typography>
                      {actual ? (
                        <Chip
                          size="small"
                          color="success"
                          icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                          label="Cargada"
                        />
                      ) : (
                        <Chip size="small" color="warning" variant="outlined" label="Pendiente" />
                      )}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      {slot.ayuda}
                    </Typography>
                    {actual?.photoUrl ? (
                      <Box sx={{ maxWidth: 220 }}>
                        <ImagesWithZoom src={actual.photoUrl} alt={slot.titulo} />
                        <Typography variant="caption" color="text.secondary" display="block">
                          {actual.fecha_hora ? dayjs(actual.fecha_hora).format("DD/MM/YYYY HH:mm") : ""}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Ya está cargada. Solo Activaciones puede eliminarla.
                        </Typography>
                      </Box>
                    ) : (
                      <FotosVisitaMultiUploader
                        files={seleccion}
                        setFiles={(f) => setSlot(slot.tipo, f)}
                        min={1}
                        max={1}
                        label={slot.titulo}
                      />
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </CustomPaperBox>
      }
      onClose={onClose}
      showCustomButtons
      customButtons={
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 1,
            width: "100%",
            px: 1,
          }}
        >
          <Button onClick={onClose} color="inherit">
            Cancelar
          </Button>
          <Button onClick={handleConfirm} variant="contained" color="primary" disabled={!haySeleccion}>
            Guardar fotos
          </Button>
        </Box>
      }
    />
  );
}
